// สัตว์อพยพและเส้นทางของแต่ละประชากร (docs/PLAN.md ข้อ 4.3 และเฟส 3)
// GBIF ไม่แยกแหล่งผสมพันธุ์กับแหล่งหากินนอกฤดู จึงคัดชนิดที่อพยพชัดเจนไว้เอง
// แหล่ง a คือที่เกิดและผสมพันธุ์ แหล่ง b คือที่ไปหากินนอกฤดูผสมพันธุ์ กรอบพิกัดหยาบๆ [ใต้, ตะวันตก, เหนือ, ตะวันออก)

export type Box = readonly [number, number, number, number];

// จุดแวะหรือจุดที่เส้นทางผ่าน [ละติจูด, ลองจิจูด, ชื่อ] จุดที่ไม่มีชื่อใช้กำหนดรูปเส้นทางเท่านั้น
// เส้นทางที่ข้ามเส้นแบ่งเขตวันสากลใส่จุดที่ลองจิจูด ±179.9 คู่กัน เส้นบนแผนที่จะต่อกันที่ขอบพอดี
export type Waypoint = readonly [number, number, string?];
export type Flyway = readonly Waypoint[];

export interface MigrationRoute {
  a: readonly Box[];
  b: readonly Box[];
  aNameTh: string;
  bNameTh: string;
  aMonths: readonly [number, number]; // เดือนที่อยู่ที่แหล่ง a (1–12 วนข้ามปีได้ เช่น [12, 4])
  via?: readonly Flyway[]; // เส้นทางจริงจาก a ไป b (มีได้หลายสาย ใช้สายที่ทำให้เดินทางสั้นที่สุด) ไม่ระบุคือไปตรงๆ
  viaBack?: readonly Flyway[]; // เส้นทางขากลับจาก b ไป a ถ้าต่างจากขาไป (ไม่ระบุคือย้อนทางขาไป)
}

export interface Migrant {
  sci: string;
  nameTh: string;
  aKindTh?: string; // ค่าเริ่มต้น "แหล่งผสมพันธุ์"
  bKindTh?: string; // ค่าเริ่มต้น "แหล่งหากินนอกฤดูผสมพันธุ์"
  routes: readonly MigrationRoute[];
  fact: string;
  // anadromous = เกิดในแม่น้ำ (a) ลงทะเล (b) ครั้งเดียวตอนโตเต็มวัย แล้วกลับมาวางไข่ที่แม่น้ำสายที่เกิด (เช่น ปลาแซลมอน)
  // natal_beach = ฟักบนหาด (a) ว่ายออกทะเลไปหากิน (b) โตแล้วกลับมาวางไข่ที่หาดที่ตัวเองฟักทุกไม่กี่ปี (เต่าทะเล)
  // ไม่ระบุ = ย้ายไปมาระหว่างสองแหล่งตามฤดูทุกปี
  cycle?: "anadromous" | "natal_beach";
}

const NORTH_SUMMER = [4, 9] as const;

export const MIGRANTS: readonly Migrant[] = [
  // ---------- นก ----------
  {
    sci: "Hirundo rustica",
    nameTh: "นกนางแอ่นบ้าน",
    routes: [
      { a: [[35, 90, 60, 145]], b: [[-10, 92, 22, 125]], aNameTh: "จีนตอนเหนือและไซบีเรีย", bNameTh: "เอเชียตะวันออกเฉียงใต้", aMonths: NORTH_SUMMER, via: [[[36, 116, "ที่ราบภาคเหนือของจีน"], [23, 113, "จีนตอนใต้"]]] },
      { a: [[35, -10, 65, 45]], b: [[-35, 10, 0, 42]], aNameTh: "ยุโรป", bNameTh: "แอฟริกาตอนใต้", aMonths: NORTH_SUMMER, via: [[[37, -5, "คาบสมุทรไอบีเรีย"], [22, 0, "ทะเลทรายสะฮารา"], [4, 12, "แคเมอรูน"], [-6, 22, "ลุ่มแม่น้ำคองโก"]]] },
      { a: [[30, -125, 60, -65]], b: [[-35, -75, 10, -40]], aNameTh: "อเมริกาเหนือ", bNameTh: "อเมริกาใต้", aMonths: NORTH_SUMMER, via: [[[27, -97, "ชายฝั่งอ่าวเม็กซิโก"], [14, -87, "อเมริกากลาง"], [5, -74, "โคลอมเบีย"]]] },
    ],
    fact: "นกนางแอ่นบ้านที่ทำรังในจีนและไซบีเรียบินลงมาหลบหนาวในไทย ส่วนตัวที่ทำรังในยุโรปบินข้ามทะเลทรายสะฮาราไปหลบหนาวในแอฟริกา",
  },
  {
    sci: "Limosa lapponica",
    nameTh: "นกปากแอ่นหางลาย",
    routes: [
      { a: [[58, -170, 72, -150]], b: [[-47, 140, -10, 180]], aNameTh: "อะแลสกา", bNameTh: "ออสเตรเลียและนิวซีแลนด์", aMonths: [5, 8], via: [[[45, -168, "กลางมหาสมุทรแปซิฟิกเหนือ"], [5, -175], [-10, -179.9], [-10, 179.9], [-17, 178, "หมู่เกาะฟิจิ"]]], viaBack: [[[-20, 155], [35, 122, "ทะเลเหลือง"], [52, 165], [57, 179.9], [57, -179.9]]] },
      { a: [[60, 60, 76, 180]], b: [[-35, 95, 10, 155]], aNameTh: "ไซบีเรีย", bNameTh: "เอเชียตะวันออกเฉียงใต้และออสเตรเลีย", aMonths: [5, 8], via: [[[37, 122, "ทะเลเหลือง"], [14, 121, "ฟิลิปปินส์"]]] },
      { a: [[65, 10, 75, 60]], b: [[0, -20, 55, 10]], aNameTh: "อาร์กติกของยุโรป", bNameTh: "ยุโรปตะวันตกและแอฟริกาตะวันตก", aMonths: [5, 8], via: [[[54, 8, "ทะเลวัดเดน"], [20, -16, "แหล่งน้ำตื้นบังดาร์กแก็ง มอริเตเนีย"]]] },
    ],
    fact: "ในปี 2022 นกปากแอ่นหางลายตัวหนึ่งบินรวดเดียวจากอะแลสกาถึงแทสเมเนียไกลกว่า 13,000 กม. ภายในราว 11 วันโดยไม่หยุดพักเลย",
  },
  {
    sci: "Sterna paradisaea",
    nameTh: "นกนางนวลแกลบอาร์กติก",
    routes: [{ a: [[55, -180, 82, 180]], b: [[-78, -180, -55, 180]], aNameTh: "อาร์กติก", bNameTh: "ทะเลรอบทวีปแอนตาร์กติกา", aMonths: [5, 8], via: [[[50, -35, "กลางมหาสมุทรแอตแลนติกเหนือ"], [10, -22, "นอกชายฝั่งแอฟริกาตะวันตก"], [-35, 5, "แอตแลนติกใต้"]]], viaBack: [[[-30, -25, "แอตแลนติกใต้"], [0, -30, "แนวเส้นศูนย์สูตร"], [40, -40, "แอตแลนติกเหนือ"]]] }],
    fact: "นกนางนวลแกลบอาร์กติกบินไปกลับระหว่างอาร์กติกกับแอนตาร์กติกาทุกปี บางตัวบินรวมกว่า 70,000 กม. ต่อปี จึงได้เห็นฤดูร้อนสองครั้งในหนึ่งปี",
  },
  {
    sci: "Ciconia ciconia",
    nameTh: "นกกระสาขาว",
    routes: [{ a: [[35, -10, 58, 42]], b: [[-35, -20, 15, 45]], aNameTh: "ยุโรป", bNameTh: "แอฟริกา", aMonths: [3, 8], via: [[[36, -5.6, "ช่องแคบยิบรอลตาร์"], [22, -5, "ทะเลทรายสะฮารา"], [14, -4, "ทุ่งซาเฮล"]], [[41, 29, "ช่องแคบบอสพอรัส"], [32, 35.5, "หุบเขาจอร์แดน"], [30, 32.5, "คอคอดสุเอซ"], [15, 33, "ซูดาน"], [0, 35, "แอฟริกาตะวันออก"]]] }],
    fact: "นกกระสาขาวร่อนไปกับอากาศร้อนที่ลอยขึ้นจากพื้นดินและเลี่ยงการบินข้ามทะเล จึงข้ามจากยุโรปไปแอฟริกาตรงช่องแคบยิบรอลตาร์และช่องแคบบอสพอรัส",
  },
  {
    sci: "Calidris pygmaea",
    nameTh: "นกชายเลนปากช้อน",
    routes: [{ a: [[60, 170, 68, 180], [60, -180, 68, -170]], b: [[10, 88, 24, 106]], aNameTh: "คาบสมุทรชูคอตกา รัสเซีย", bNameTh: "อ่าวไทย อ่าวเมาะตะมะ และบังกลาเทศ", aMonths: [6, 8], via: [[[55, 158, "คาบสมุทรคัมชัตคา"], [35.8, 126.6, "หาดเลนแซมันกึม เกาหลีใต้"], [32.5, 121, "หาดเลนรูตง มณฑลเจียงซู"], [20.5, 106.5, "ปากแม่น้ำแดง เวียดนาม"]]] }],
    fact: "นกชายเลนปากช้อนเหลือในธรรมชาติเพียงไม่กี่ร้อยคู่ ทำรังที่คาบสมุทรชูคอตกาของรัสเซีย และบินมาหลบหนาวตามนาเกลือริมอ่าวไทยตอนใน",
  },
  {
    sci: "Anser indicus",
    nameTh: "ห่านหัวลาย",
    routes: [{ a: [[30, 70, 52, 110]], b: [[8, 68, 30, 95]], aNameTh: "ที่ราบสูงทิเบตและมองโกเลีย", bNameTh: "อินเดีย", aMonths: NORTH_SUMMER, via: [[[28, 87, "เทือกเขาหิมาลัย"]]] }],
    fact: "ห่านหัวลายบินข้ามเทือกเขาหิมาลัยระหว่างที่ราบสูงในเอเชียกลางกับอินเดีย เป็นนกที่บินได้สูงที่สุดชนิดหนึ่งในโลก",
  },
  {
    sci: "Lanius cristatus",
    nameTh: "นกอีเสือสีน้ำตาล",
    routes: [{ a: [[35, 90, 65, 145]], b: [[-10, 75, 25, 125]], aNameTh: "ไซบีเรียและจีนตอนเหนือ", bNameTh: "เอเชียใต้และเอเชียตะวันออกเฉียงใต้", aMonths: [5, 8], via: [[[31, 113, "จีนตอนกลาง"], [22, 107, "จีนตอนใต้"]]] }],
    fact: "นกอีเสือสีน้ำตาลเป็นนกอพยพที่พบได้ทั่วไปในไทยช่วงฤดูหนาว และชอบเสียบเหยื่อไว้กับหนามไม้เก็บไว้กินทีหลัง",
  },
  {
    sci: "Falco amurensis",
    nameTh: "เหยี่ยวตีนแดง",
    routes: [{ a: [[38, 100, 55, 140]], b: [[-35, 15, -10, 40]], aNameTh: "จีนตอนเหนือและรัสเซียตะวันออกไกล", bNameTh: "แอฟริกาตอนใต้", aMonths: [5, 9], via: [[[26, 94, "นาคาแลนด์ อินเดีย"], [15, 65, "ทะเลอาหรับ"], [2, 42, "โซมาเลีย"], [-8, 35, "แทนซาเนีย"]]] }],
    fact: "เหยี่ยวตีนแดงบินจากจีนตอนเหนือไปหลบหนาวในแอฟริกาตอนใต้ ระหว่างทางบินข้ามมหาสมุทรอินเดียหลายพันกิโลเมตร",
  },
  {
    sci: "Pandion haliaetus",
    nameTh: "เหยี่ยวออสเปร",
    routes: [
      { a: [[45, -10, 70, 45]], b: [[0, -20, 20, 20]], aNameTh: "ยุโรปเหนือ", bNameTh: "แอฟริกาตะวันตก", aMonths: NORTH_SUMMER, via: [[[40, -4, "สเปน"], [24, -8, "ทะเลทรายสะฮารา"]]] },
      { a: [[40, -130, 65, -60]], b: [[-20, -80, 10, -40]], aNameTh: "อเมริกาเหนือ", bNameTh: "อเมริกาใต้", aMonths: NORTH_SUMMER, via: [[[27, -81, "ฟลอริดา"], [20, -75, "คิวบาและฮิสปันโยลา"], [8, -67, "เวเนซุเอลา"]]] },
      { a: [[45, 90, 65, 145]], b: [[0, 90, 25, 125]], aNameTh: "ไซบีเรียและจีนตอนเหนือ", bNameTh: "เอเชียตะวันออกเฉียงใต้", aMonths: NORTH_SUMMER, via: [[[31, 120, "ชายฝั่งจีนตะวันออก"], [22, 112, "จีนตอนใต้"]]] },
    ],
    fact: "เหยี่ยวออสเปรที่ทำรังในเขตหนาวบินไปหลบหนาวในเขตร้อนทุกปี ส่วนประชากรในเขตร้อนและออสเตรเลียอยู่ที่เดิมทั้งปี",
  },
  // ---------- แมลง ----------
  {
    sci: "Danaus plexippus",
    nameTh: "ผีเสื้อโมนาร์ช",
    bKindTh: "แหล่งหลบหนาว",
    routes: [
      { a: [[30, -105, 50, -65]], b: [[18, -101, 21, -99]], aNameTh: "สหรัฐอเมริกาและแคนาดา", bNameTh: "ป่าสนบนภูเขาในเม็กซิโก", aMonths: NORTH_SUMMER, via: [[[37, -97, "แคนซัสและโอคลาโฮมา"], [30, -98, "เท็กซัส"], [25, -100, "ภาคเหนือของเม็กซิโก"]]] },
      { a: [[39, -124, 49, -114]], b: [[34, -121.5, 38, -119]], aNameTh: "ออริกอน วอชิงตัน และไอดาโฮ", bNameTh: "ดงยูคาลิปตัสริมชายฝั่งแคลิฟอร์เนีย", aMonths: NORTH_SUMMER, via: [[[39.5, -121.5, "หุบเขาเซ็นทรัลแวลลีย์ แคลิฟอร์เนีย"]]] },
    ],
    fact: "ผีเสื้อโมนาร์ชที่บินลงใต้ไปหลบหนาวในป่าสนบนภูเขาของเม็กซิโกเป็นรุ่นที่ไม่เคยไปที่นั่นมาก่อน การเดินทางไปกลับครบรอบต้องใช้ผีเสื้อหลายรุ่น",
  },
  {
    sci: "Vanessa cardui",
    nameTh: "ผีเสื้อนางแต้มสี",
    aKindTh: "แหล่งขยายพันธุ์ฤดูร้อน",
    bKindTh: "แหล่งขยายพันธุ์ฤดูหนาว",
    routes: [{ a: [[40, -10, 62, 40]], b: [[5, -17, 20, 40]], aNameTh: "ยุโรป", bNameTh: "ทุ่งซาเฮลใต้ทะเลทรายสะฮารา", aMonths: [5, 9], via: [[[37, 3, "ทะเลเมดิเตอร์เรเนียน"], [24, 5, "ทะเลทรายสะฮารา"]]] }],
    fact: "ผีเสื้อนางแต้มสีอพยพไปกลับระหว่างแอฟริกากับยุโรปไกลราว 12,000–14,000 กม. ต่อปี ไกลกว่าผีเสื้อโมนาร์ช โดยใช้ผีเสื้อราว 6 รุ่นต่อกัน และบินสูงหลายร้อยเมตรไปกับลมจนเรดาร์ตรวจจับได้",
  },
  {
    sci: "Vanessa atalanta",
    nameTh: "ผีเสื้อแดง",
    aKindTh: "แหล่งขยายพันธุ์ฤดูร้อน",
    bKindTh: "แหล่งหลบหนาว",
    routes: [{ a: [[48, -5, 62, 30]], b: [[36, -10, 45, 30]], aNameTh: "ยุโรปตอนเหนือ", bNameTh: "ชายฝั่งทะเลเมดิเตอร์เรเนียน", aMonths: [5, 9], via: [[[46.1, 6.8, "ช่องเขาในเทือกเขาแอลป์"]]] }],
    fact: "ฤดูใบไม้ร่วงผีเสื้อแดงนับหมื่นตัวบินข้ามช่องเขาในเทือกเขาแอลป์ลงใต้ นักวิจัยที่สถานีบนช่องเขาในสวิตเซอร์แลนด์นับได้ปีละหลายพันตัว",
  },
  {
    sci: "Parantica sita",
    nameTh: "ผีเสื้อเสือสีน้ำตาลแดง",
    aKindTh: "แหล่งขยายพันธุ์ฤดูร้อนบนภูเขา",
    bKindTh: "แหล่งหลบหนาว",
    routes: [{ a: [[33, 130, 43, 142]], b: [[22, 119, 29, 130]], aNameTh: "ภูเขาตอนกลางของญี่ปุ่น", bNameTh: "หมู่เกาะนันเซ (โอกินาวา) และไต้หวัน", aMonths: [6, 9], via: [[[31.3, 130.5, "คาบสมุทรซัตสึมะ เกาะคิวชู"]]] }],
    fact: "นักวิจัยในญี่ปุ่นเขียนรหัสลงบนปีกผีเสื้อเสือสีน้ำตาลแดงไว้นับแสนตัว บางตัวถูกพบอีกครั้งที่ฮ่องกงไกลกว่า 2,000 กม. จากจุดที่ปล่อย",
  },
  {
    sci: "Tirumala septentrionis",
    nameTh: "ผีเสื้อเสือน้ำเงินเข้ม",
    aKindTh: "แหล่งหลบมรสุม",
    bKindTh: "แหล่งขยายพันธุ์หน้ามรสุม",
    routes: [{ a: [[10, 78, 18, 82]], b: [[8, 74, 15, 77.5]], aNameTh: "ที่ราบและเทือกเขาฆาฏตะวันออก อินเดีย", bNameTh: "เทือกเขาฆาฏตะวันตก อินเดีย", aMonths: [10, 4], via: [[[10.8, 76.7, "ช่องเขาปาลักกาด"]]] }],
    fact: "ผีเสื้อเสือและผีเสื้อจรกาในอินเดียตอนใต้อพยพเป็นฝูงใหญ่ระหว่างเทือกเขาฆาฏตะวันตกกับฝั่งตะวันออกตามฤดูมรสุม บินผ่านช่องเขาปาลักกาดวันละนับหมื่นตัว",
  },
  {
    sci: "Euploea core",
    nameTh: "ผีเสื้อจรกาธรรมดา",
    aKindTh: "แหล่งหลบมรสุม",
    bKindTh: "แหล่งขยายพันธุ์หน้ามรสุม",
    routes: [{ a: [[10, 78, 18, 82]], b: [[8, 74, 15, 77.5]], aNameTh: "ที่ราบและเทือกเขาฆาฏตะวันออก อินเดีย", bNameTh: "เทือกเขาฆาฏตะวันตก อินเดีย", aMonths: [10, 4], via: [[[10.8, 76.7, "ช่องเขาปาลักกาด"]]] }],
    fact: "ผีเสื้อจรกาธรรมดากินพืชที่มีสารพิษตั้งแต่เป็นหนอน ตัวเต็มวัยจึงมีรสขมจนนกไม่อยากกิน และบินช้าๆ อวดสีได้อย่างปลอดภัยแม้ระหว่างอพยพเป็นฝูงใหญ่",
  },
  // ---------- สัตว์ทะเล ----------
  {
    sci: "Megaptera novaeangliae",
    nameTh: "วาฬหลังค่อม",
    aKindTh: "แหล่งผสมพันธุ์และออกลูก",
    bKindTh: "แหล่งหากิน",
    routes: [
      { a: [[15, -165, 28, -150]], b: [[45, -180, 65, -130]], aNameTh: "ทะเลรอบหมู่เกาะฮาวาย", bNameTh: "อ่าวอะแลสกาและทะเลแบริ่ง", aMonths: [12, 4] },
      { a: [[10, -75, 25, -60]], b: [[45, -70, 75, -10]], aNameTh: "ทะเลแคริบเบียน", bNameTh: "แอตแลนติกเหนือ", aMonths: [12, 4], via: [[[32.3, -64.8, "หมู่เกาะเบอร์มิวดา"]]] },
      { a: [[-28, 145, -10, 160]], b: [[-70, 100, -55, 180]], aNameTh: "ชายฝั่งตะวันออกของออสเตรเลีย", bNameTh: "มหาสมุทรใต้", aMonths: [6, 10], via: [[[-34, 152, "นอกชายฝั่งซิดนีย์"], [-44, 150, "ทะเลแทสมัน"]]] },
      { a: [[-22, 113, -14, 125]], b: [[-68, 70, -55, 130]], aNameTh: "ชายฝั่งคิมเบอร์ลีย์ ออสเตรเลียตะวันตก", bNameTh: "มหาสมุทรใต้", aMonths: [6, 10], via: [[[-22.5, 113.5, "แนวปะการังนิงกาลู"], [-35, 115, "แหลมลูวิน"]]] },
      { a: [[-23, -177, -15, -172]], b: [[-70, -180, -60, -150]], aNameTh: "หมู่เกาะตองงา", bNameTh: "ทะเลรอสส์และมหาสมุทรใต้", aMonths: [7, 10] },
      { a: [[-20, -40, -12, -36]], b: [[-58, -45, -50, -30]], aNameTh: "หมู่เกาะอโบรลโยส บราซิล", bNameTh: "รอบเกาะเซาท์จอร์เจีย", aMonths: [7, 11] },
      { a: [[17, -112, 24, -104]], b: [[34, -126, 46, -120]], aNameTh: "ชายฝั่งแปซิฟิกของเม็กซิโก", bNameTh: "นอกชายฝั่งแคลิฟอร์เนียและออริกอน", aMonths: [12, 4], via: [[[23, -110, "ปลายคาบสมุทรบาฮากาลิฟอร์เนีย"]]] },
    ],
    fact: "วาฬหลังค่อมกินอาหารในทะเลเย็นใกล้ขั้วโลกช่วงฤดูร้อน แล้วว่ายไปผสมพันธุ์และออกลูกในทะเลเขตร้อน ระหว่างอยู่ที่นั่นแทบไม่กินอะไรเลย",
  },
  {
    sci: "Eschrichtius robustus",
    nameTh: "วาฬสีเทา",
    aKindTh: "แหล่งผสมพันธุ์และออกลูก",
    bKindTh: "แหล่งหากิน",
    routes: [{ a: [[22, -118, 32, -110]], b: [[55, -180, 72, -155]], aNameTh: "ลากูนในบาฮากาลิฟอร์เนีย เม็กซิโก", bNameTh: "ทะเลแบริ่งและทะเลชุกชี", aMonths: [12, 3], via: [[[34, -121, "นอกชายฝั่งแคลิฟอร์เนีย"], [46, -125, "ชายฝั่งออริกอนและวอชิงตัน"], [57, -145, "อ่าวอะแลสกา"], [54.3, -165, "ช่องแคบยูนิมัก หมู่เกาะอะลูเชียน"]]] }],
    fact: "วาฬสีเทาว่ายไปกลับระหว่างทะเลแบริ่งกับลากูนในเม็กซิโกปีละราว 16,000–20,000 กม. เป็นการอพยพที่ไกลที่สุดชนิดหนึ่งของสัตว์เลี้ยงลูกด้วยนม",
  },
  {
    sci: "Eubalaena australis",
    nameTh: "วาฬไรต์ใต้",
    aKindTh: "อ่าวที่ผสมพันธุ์และออกลูก",
    bKindTh: "แหล่งหากิน",
    routes: [
      { a: [[-44, -66, -41, -62]], b: [[-56, -42, -50, -30]], aNameTh: "คาบสมุทรบัลเดส อาร์เจนตินา", bNameTh: "รอบเกาะเซาท์จอร์เจีย", aMonths: [6, 11], via: [[[-51.7, -59, "หมู่เกาะฟอล์กแลนด์"]]] },
      { a: [[-35, 18, -33, 23]], b: [[-60, 0, -45, 30]], aNameTh: "อ่าวเฮอร์มานัส แอฟริกาใต้", bNameTh: "มหาสมุทรใต้", aMonths: [6, 11] },
      { a: [[-33, 128, -31, 134]], b: [[-60, 110, -45, 150]], aNameTh: "อ่าวเกรตออสเตรเลียนไบต์", bNameTh: "มหาสมุทรใต้", aMonths: [6, 10] },
    ],
    fact: "วาฬไรต์ได้ชื่อนี้เพราะนักล่าวาฬสมัยก่อนเห็นว่าเป็นวาฬที่ \"ใช่\" สำหรับล่า ว่ายช้า อยู่ใกล้ฝั่ง และลอยน้ำหลังตาย จนเกือบสูญพันธุ์ ปัจจุบันกลับมาออกลูกในอ่าวเดิมทุกปี",
  },
  {
    sci: "Eubalaena glacialis",
    nameTh: "วาฬไรต์แอตแลนติกเหนือ",
    aKindTh: "แหล่งออกลูก",
    bKindTh: "แหล่งหากิน",
    routes: [{ a: [[28, -82, 32, -79]], b: [[40, -71, 46, -63]], aNameTh: "ชายฝั่งจอร์เจียและฟลอริดา สหรัฐอเมริกา", bNameTh: "อ่าวเมนและอ่าวฟันดี", aMonths: [12, 3], via: [[[35.2, -75.3, "แหลมแฮตเทอราส"], [41.5, -69.8, "แหลมเคปคอด"]]] }],
    fact: "วาฬไรต์แอตแลนติกเหนือเหลือในโลกราว 370 ตัว เส้นทางอพยพเลียบชายฝั่งตะวันออกของสหรัฐฯ ตัดผ่านเส้นทางเดินเรือและแนวเครื่องมือประมง สาเหตุการตายหลักจึงเป็นเรือชนและติดเชือกประมง",
  },
  {
    sci: "Balaenoptera musculus",
    nameTh: "วาฬสีน้ำเงิน",
    aKindTh: "แหล่งผสมพันธุ์และออกลูก",
    bKindTh: "แหล่งหากิน",
    routes: [{ a: [[7, -92, 11, -86]], b: [[32, -125, 40, -118]], aNameTh: "โดมคอสตาริกา", bNameTh: "นอกชายฝั่งแคลิฟอร์เนีย", aMonths: [12, 5], via: [[[22.8, -110, "ปลายคาบสมุทรบาฮากาลิฟอร์เนีย"]]] }],
    fact: "วาฬสีน้ำเงินเป็นสัตว์ที่ใหญ่ที่สุดเท่าที่เคยมีมาบนโลก กินเคยวันละหลายตัน และว่ายจากแหล่งหากินนอกแคลิฟอร์เนียลงไปออกลูกที่ \"โดมคอสตาริกา\" บริเวณที่น้ำเย็นยกตัวขึ้นกลางทะเลเขตร้อน",
  },
  {
    sci: "Balaena mysticetus",
    nameTh: "วาฬหัวคันศร",
    aKindTh: "แหล่งหลบหนาวใต้น้ำแข็ง",
    bKindTh: "แหล่งหากินฤดูร้อน",
    routes: [{ a: [[57, -180, 64, -165]], b: [[69, -145, 73, -120]], aNameTh: "ทะเลแบริ่ง", bNameTh: "ทะเลโบฟอร์ต", aMonths: [11, 4], via: [[[65.8, -168.7, "ช่องแคบแบริ่ง"], [71.5, -156.5, "แหลมแบร์โรว์ อะแลสกา"]]] }],
    fact: "วาฬหัวคันศรอายุยืนกว่า 200 ปี ใช้หัวขนาดใหญ่ชนน้ำแข็งหนาเกือบครึ่งเมตรให้แตกเพื่อขึ้นมาหายใจ ระหว่างอพยพตามรอยแยกของน้ำแข็งทะเล",
  },
  {
    sci: "Delphinapterus leucas",
    nameTh: "วาฬเบลูกา",
    aKindTh: "ปากแม่น้ำที่ออกลูกฤดูร้อน",
    bKindTh: "แหล่งหลบหนาว",
    routes: [{ a: [[56, -96, 60, -86]], b: [[60, -75, 64, -60]], aNameTh: "ปากแม่น้ำเชอร์ชิลล์และเนลสัน อ่าวฮัดสัน", bNameTh: "ช่องแคบฮัดสันและทะเลแลบราดอร์", aMonths: [6, 9], via: [[[63, -82, "ตอนเหนือของอ่าวฮัดสัน"]]] }],
    fact: "ฤดูร้อนวาฬเบลูกานับหมื่นตัวเข้ามาในปากแม่น้ำของอ่าวฮัดสัน เพื่อออกลูกในน้ำอุ่นกว่าและถูตัวกับพื้นกรวดให้ผิวหนังเก่าหลุดลอก ก่อนออกทะเลไปหลบหนาวเมื่อน้ำแข็งเริ่มปกคลุม",
  },
  {
    sci: "Monodon monoceros",
    nameTh: "นาร์วาล",
    aKindTh: "ฟยอร์ดที่อยู่ฤดูร้อน",
    bKindTh: "แหล่งหากินฤดูหนาวใต้น้ำแข็ง",
    routes: [{ a: [[70, -95, 77, -70]], b: [[66, -62, 72, -55]], aNameTh: "ฟยอร์ดในอาร์กติกแคนาดา", bNameTh: "อ่าวแบฟฟิน", aMonths: [7, 9], via: [[[74.3, -80, "ช่องแคบแลงแคสเตอร์"]]] }],
    fact: "ฤดูหนาวนาร์วาลดำน้ำลึกกว่า 1,500 เมตรใต้แผ่นน้ำแข็งของอ่าวแบฟฟินเพื่อกินปลาแฮลิบัต ส่วนงายาวของตัวผู้ที่จริงคือฟันที่ยื่นออกมาและรับรู้ความเปลี่ยนแปลงของน้ำได้",
  },
  {
    sci: "Dermochelys coriacea",
    nameTh: "เต่ามะเฟือง",
    aKindTh: "หาดวางไข่",
    bKindTh: "แหล่งหากิน",
    cycle: "natal_beach",
    routes: [
      { a: [[5, 95, 10, 99]], b: [[-40, 60, -10, 110]], aNameTh: "ชายฝั่งทะเลอันดามัน", bNameTh: "มหาสมุทรอินเดียตอนใต้", aMonths: [11, 2] },
      { a: [[-5, 128, 3, 141]], b: [[30, -130, 45, -118]], aNameTh: "ปาปัว อินโดนีเซีย", bNameTh: "นอกชายฝั่งแคลิฟอร์เนีย", aMonths: [6, 9], via: [[[8, 150, "หมู่เกาะไมโครนีเซีย"], [25, 179.9], [25, -179.9], [33, -155, "ทางเหนือของหมู่เกาะฮาวาย"]]] },
      { a: [[0, -65, 15, -50]], b: [[35, -75, 55, -20]], aNameTh: "ทะเลแคริบเบียนและเฟรนช์เกียนา", bNameTh: "แอตแลนติกเหนือ", aMonths: [3, 7], via: [[[28, -60, "ทะเลซาร์กัสโซ"]]] },
    ],
    fact: "เต่ามะเฟืองบางตัวว่ายข้ามมหาสมุทรแปซิฟิกจากหาดวางไข่ในอินโดนีเซียไปหากินแมงกะพรุนนอกชายฝั่งแคลิฟอร์เนีย",
  },
  {
    sci: "Chelonia mydas",
    nameTh: "เต่าตนุ",
    aKindTh: "หาดวางไข่",
    bKindTh: "แหล่งหากิน",
    cycle: "natal_beach",
    routes: [
      { a: [[-8.5, -15, -7.5, -14]], b: [[-25, -50, -2, -34]], aNameTh: "เกาะแอสเซนชัน", bNameTh: "ชายฝั่งบราซิล", aMonths: [12, 5] },
      { a: [[10, -84, 11.5, -83]], b: [[12, -84, 16, -81]], aNameTh: "ชายหาดตอร์ตูเกโร คอสตาริกา", bNameTh: "แนวหญ้าทะเลนอกชายฝั่งนิการากัว", aMonths: [6, 10] },
      { a: [[4.5, 103, 6, 103.8]], b: [[5, 116, 12, 124]], aNameTh: "ชายหาดตรังกานูและเกาะเรดัง มาเลเซีย", bNameTh: "ทะเลซูลูและฟิลิปปินส์", aMonths: [5, 9], via: [[[7, 110, "ทะเลจีนใต้"]]] },
      { a: [[-12, 143.8, -11.4, 144.2]], b: [[-24, 146, -14, 153]], aNameTh: "เกาะเรน ออสเตรเลีย", bNameTh: "แนวปะการังเกรตแบร์ริเออร์", aMonths: [11, 3] },
    ],
    fact: "เต่าตนุจากเกาะแอสเซนชันกลางมหาสมุทรแอตแลนติกว่ายไปกลับชายฝั่งบราซิลกว่า 2,000 กม. เพื่อกลับมาวางไข่บนเกาะเล็กๆ ที่หาเจอได้ด้วยสนามแม่เหล็กโลก",
  },
  {
    sci: "Caretta caretta",
    nameTh: "เต่าหัวค้อน",
    aKindTh: "หาดวางไข่",
    bKindTh: "แหล่งหากิน",
    cycle: "natal_beach",
    routes: [
      { a: [[25, -82, 34, -78]], b: [[30, -45, 42, -20]], aNameTh: "ชายหาดฟลอริดาและแคโรไลนา", bNameTh: "แอตแลนติกเหนือแถวหมู่เกาะอะซอเรส", aMonths: [5, 8], via: [[[34, -75, "กระแสน้ำกัลฟ์สตรีม"], [38, -28, "หมู่เกาะอะซอเรส"]]] },
      { a: [[30, 130, 35, 140]], b: [[23, -117, 32, -110]], aNameTh: "ชายหาดทางใต้ของญี่ปุ่น", bNameTh: "นอกชายฝั่งบาฮากาลิฟอร์เนีย เม็กซิโก", aMonths: [5, 8], via: [[[35, 142, "กระแสน้ำคุโรชิโอ"], [38, 179.9], [38, -179.9], [35, -150, "กลางมหาสมุทรแปซิฟิกเหนือ"]]] },
      { a: [[37.5, 20.5, 38, 21]], b: [[31, 10, 35, 25]], aNameTh: "เกาะซาคินทอส กรีซ", bNameTh: "อ่าวกาเบสและชายฝั่งลิเบีย", aMonths: [6, 8] },
    ],
    fact: "ลูกเต่าหัวค้อนที่ฟักบนหาดในญี่ปุ่นว่ายข้ามมหาสมุทรแปซิฟิกไปโตนอกชายฝั่งเม็กซิโก แล้วว่ายกลับมาวางไข่ที่ญี่ปุ่นเมื่อโตเต็มวัย ไปกลับรวมกว่า 20,000 กม.",
  },
  {
    sci: "Lepidochelys olivacea",
    nameTh: "เต่าหญ้า",
    aKindTh: "หาดวางไข่",
    bKindTh: "แหล่งหากิน",
    cycle: "natal_beach",
    routes: [
      { a: [[19, 84.5, 20.8, 87.2]], b: [[0, 78, 12, 95]], aNameTh: "ชายหาดโอริสสา อินเดีย", bNameTh: "อ่าวเบงกอลตอนใต้และศรีลังกา", aMonths: [1, 4], via: [[[15, 84, "อ่าวเบงกอล"]]] },
      { a: [[9.5, -86, 10.5, -85.5]], b: [[-15, -100, 5, -80]], aNameTh: "ชายหาดโอสติโอนัล คอสตาริกา", bNameTh: "แปซิฟิกตะวันออกเขตร้อน", aMonths: [7, 12] },
    ],
    fact: "เต่าหญ้าตัวเมียนับหมื่นตัวขึ้นวางไข่บนหาดเดียวกันพร้อมกันในไม่กี่คืน เรียกว่า \"อาร์ริบาดา\" ซึ่งเป็นภาษาสเปนแปลว่าการมาถึง",
  },
  {
    sci: "Lepidochelys kempii",
    nameTh: "เต่าหญ้าแอตแลนติก",
    aKindTh: "หาดวางไข่",
    bKindTh: "แหล่งหากิน",
    cycle: "natal_beach",
    routes: [{ a: [[22.5, -98, 24.5, -97.4]], b: [[28, -94, 31, -83]], aNameTh: "ชายหาดรันโชนูเอโว เม็กซิโก", bNameTh: "ชายฝั่งลุยเซียนาและฟลอริดา อ่าวเม็กซิโก", aMonths: [4, 7], via: [[[26.5, -97, "ชายฝั่งเท็กซัส"]]] }],
    fact: "เต่าหญ้าแอตแลนติกเกือบทั้งโลกวางไข่บนหาดช่วงเดียวในเม็กซิโก เคยเหลือตัวเมียขึ้นวางไข่ไม่ถึงพันตัวในปี 1985 ก่อนการอนุรักษ์จะช่วยให้ฟื้นตัว",
  },
  {
    sci: "Natator depressus",
    nameTh: "เต่าหลังแบน",
    aKindTh: "หาดวางไข่",
    bKindTh: "แหล่งหากิน",
    cycle: "natal_beach",
    routes: [{ a: [[-23, 113, -19, 120]], b: [[-16, 121, -12, 130]], aNameTh: "ชายหาดพิลบารา ออสเตรเลียตะวันตก", bNameTh: "ทะเลติมอร์ นอกชายฝั่งคิมเบอร์ลีย์", aMonths: [10, 2] }],
    fact: "เต่าหลังแบนวางไข่ที่ออสเตรเลียเท่านั้น และเป็นเต่าทะเลชนิดเดียวที่ลูกเต่าไม่ออกไปล่องลอยกลางมหาสมุทร แต่โตอยู่ในทะเลตื้นบนไหล่ทวีป",
  },
  // ---------- ปลาน้ำจืด ----------
  {
    sci: "Pangasianodon gigas",
    nameTh: "ปลาบึก",
    aKindTh: "แหล่งวางไข่",
    bKindTh: "แหล่งหากิน",
    routes: [{ a: [[17.5, 99.5, 21, 102.5]], b: [[10.5, 102, 14.5, 106.5]], aNameTh: "แม่น้ำโขงตอนบนแถวเชียงของ", bNameTh: "ทะเลสาบเขมร", aMonths: [4, 6], via: [[[18, 102.6, "เวียงจันทน์"], [16.5, 104.7, "นครพนมและมุกดาหาร"], [15.1, 105.8, "ปากเซ"], [13.9, 105.9, "น้ำตกคอนพะเพ็ง"], [11.6, 104.9, "พนมเปญ"]]] }],
    fact: "ปลาบึกว่ายทวนแม่น้ำโขงขึ้นไปวางไข่ทางตอนเหนือราวเดือนเมษายนถึงพฤษภาคม ชาวประมงที่เชียงของจึงมีพิธีบวงสรวงก่อนออกจับปลาบึก",
  },
  // ---------- ปลาแซลมอน (เกิดในแม่น้ำ โตในทะเล กลับมาวางไข่ที่แม่น้ำสายที่เกิด) ----------
  {
    sci: "Oncorhynchus nerka",
    nameTh: "ปลาแซลมอนซ็อกอาย",
    aKindTh: "แม่น้ำที่เกิดและวางไข่",
    bKindTh: "ทะเลที่ไปหากินจนโต",
    cycle: "anadromous",
    routes: [
      { a: [[56, -162, 61, -153]], b: [[46, -178, 55, -150]], aNameTh: "แม่น้ำและทะเลสาบรอบอ่าวบริสตอล อะแลสกา", bNameTh: "แปซิฟิกเหนือ", aMonths: [7, 10], via: [[[57.5, -158, "อ่าวบริสตอล"], [54.3, -165, "ช่องแคบยูนิมัก หมู่เกาะอะลูเชียน"]]] },
      { a: [[49, -125, 54, -118]], b: [[48, -160, 58, -136]], aNameTh: "แม่น้ำเฟรเซอร์ บริติชโคลัมเบีย", bNameTh: "อ่าวอะแลสกา", aMonths: [8, 10], via: [[[49.1, -123.3, "ปากแม่น้ำเฟรเซอร์"], [49.5, -124, "ช่องแคบจอร์เจีย"], [53, -131, "ช่องแคบเฮคาตี"]]] },
      { a: [[51, 155, 60, 163]], b: [[40, 165, 51, 180]], aNameTh: "แม่น้ำในคาบสมุทรคัมชัตคา", bNameTh: "แปซิฟิกเหนือฝั่งตะวันตก", aMonths: [7, 9] },
    ],
    fact: "ปลาแซลมอนซ็อกอายหาทางกลับไปวางไข่ที่ลำธารสายที่ตัวเองเกิดได้ด้วยกลิ่นของน้ำ ตอนว่ายทวนน้ำกลับบ้านลำตัวจะเปลี่ยนเป็นสีแดงสด และตายหลังวางไข่",
  },
  {
    sci: "Oncorhynchus tshawytscha",
    nameTh: "ปลาแซลมอนชินูก",
    aKindTh: "แม่น้ำที่เกิดและวางไข่",
    bKindTh: "ทะเลที่ไปหากินจนโต",
    cycle: "anadromous",
    routes: [
      { a: [[44, -124, 49, -114]], b: [[48, -150, 58, -128]], aNameTh: "ลุ่มแม่น้ำโคลัมเบีย", bNameTh: "อ่าวอะแลสกา", aMonths: [8, 11], via: [[[46.2, -124.1, "ปากแม่น้ำโคลัมเบีย"], [52, -131, "ชายฝั่งบริติชโคลัมเบีย"]]] },
      { a: [[61, -164, 66, -135]], b: [[54, -178, 61, -166]], aNameTh: "แม่น้ำยูคอน", bNameTh: "ทะเลแบริ่ง", aMonths: [7, 9], via: [[[62.6, -164.8, "ปากแม่น้ำยูคอน"]]] },
    ],
    fact: "ปลาแซลมอนชินูกบางตัวว่ายทวนแม่น้ำยูคอนขึ้นไปวางไข่ไกลเกือบ 3,000 กม. โดยไม่กินอะไรเลยตลอดทาง อาศัยพลังงานที่สะสมไว้ในทะเลอย่างเดียว",
  },
  {
    sci: "Salmo salar",
    nameTh: "ปลาแซลมอนแอตแลนติก",
    aKindTh: "แม่น้ำที่เกิดและวางไข่",
    bKindTh: "ทะเลที่ไปหากินจนโต",
    cycle: "anadromous",
    routes: [
      { a: [[58, 5, 71, 30]], b: [[64, -20, 74, -2]], aNameTh: "แม่น้ำในนอร์เวย์", bNameTh: "ทะเลนอร์เวย์", aMonths: [10, 12], via: [[[67, 3, "ทะเลนอร์เวย์"]]] },
      { a: [[51, -10, 59, -1]], b: [[60, -56, 67, -44]], aNameTh: "แม่น้ำในสกอตแลนด์และไอร์แลนด์", bNameTh: "นอกชายฝั่งกรีนแลนด์ตะวันตก", aMonths: [10, 12], via: [[[62, -7, "หมู่เกาะแฟโร"], [59.5, -43.5, "แหลมฟาร์เวล กรีนแลนด์"]]] },
      { a: [[44, -70, 53, -55]], b: [[57, -62, 66, -50]], aNameTh: "แม่น้ำในแคนาดาตะวันออก", bNameTh: "ทะเลแลบราดอร์", aMonths: [10, 11], via: [[[51.5, -56.5, "ช่องแคบเบลล์ไอล์"]]] },
    ],
    fact: "ปลาแซลมอนแอตแลนติกต่างจากแซลมอนแปซิฟิกตรงที่บางตัวรอดหลังวางไข่ ว่ายกลับลงทะเล แล้วกลับมาวางไข่ได้อีกรอบ",
  },
  // ---------- สัตว์บก ----------
  {
    sci: "Connochaetes taurinus",
    nameTh: "วิลเดอบีสต์ลายพาด",
    aKindTh: "แหล่งออกลูก",
    bKindTh: "แหล่งหากินหน้าแล้ง",
    routes: [{ a: [[-4, 34, -2, 36]], b: [[-2, 34, -0.5, 36]], aNameTh: "ทุ่งหญ้าทางใต้ของเซเรงเกติ", bNameTh: "มาไซมารา", aMonths: [12, 5] }],
    fact: "ลูกวิลเดอบีสต์ราวครึ่งล้านตัวเกิดในช่วงไม่กี่สัปดาห์ที่ทุ่งหญ้าทางใต้ของเซเรงเกติ และลุกขึ้นวิ่งตามแม่ได้ภายในไม่กี่นาทีหลังเกิด",
  },
  {
    sci: "Rangifer tarandus",
    nameTh: "กวางเรนเดียร์",
    aKindTh: "แหล่งออกลูก",
    bKindTh: "แหล่งหากินฤดูหนาว",
    routes: [
      { a: [[67, -150, 72, -135]], b: [[62, -150, 67, -130]], aNameTh: "ทุนดราริมมหาสมุทรอาร์กติก อะแลสกาและยูคอน", bNameTh: "ป่าสนทางใต้", aMonths: [5, 8] },
      { a: [[70, 80, 77, 120]], b: [[62, 80, 70, 120]], aNameTh: "ทุนดราคาบสมุทรไตมีร์", bNameTh: "ป่าไทกาไซบีเรีย", aMonths: [5, 8] },
    ],
    fact: "กวางเรนเดียร์ (ในอเมริกาเหนือเรียกว่าคาริบู) บางฝูงเดินทางไปกลับปีละหลายพันกิโลเมตร ไกลที่สุดในบรรดาสัตว์บก",
  },
];

export function migrantOf(sci: string): Migrant | null {
  return MIGRANTS.find((m) => m.sci === sci) ?? null;
}

export const inBox = ([s, w, n, e]: Box, lat: number, lon: number) => lat >= s && lat < n && lon >= w && lon < e;
export const inBoxes = (boxes: readonly Box[], lat: number, lon: number) => boxes.some((b) => inBox(b, lat, lon));
